import { useState } from 'react'
import { kitOrderService, type KitOrderData, type KitOrder } from '../services/kitOrderService'
import { PublicKit, PublicImage } from '../utils/publicCacheService'

interface KitOrderModalProps {
  kit: PublicKit
  kitImage?: PublicImage | null
  isOpen: boolean
  onClose: () => void
  onOrderComplete: (orderId: string) => void
  onStockUpdate?: (kitId: string, newStockCount: number) => void
}

export default function KitOrderModal({
  kit,
  kitImage,
  isOpen,
  onClose,
  onOrderComplete,
  onStockUpdate
}: KitOrderModalProps) {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    quantity: 1,
    notes: '',
    preferredPickup: true,
    deliveryAddress: '',
    paymentMethod: 'credit-card' as 'credit-card' | 'zelle' | 'venmo' | 'cash'
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [createdOrderId, setCreatedOrderId] = useState<string>('')
  const [order, setOrder] = useState<KitOrder | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked
      setFormData(prev => ({ ...prev, [name]: checked }))
    } else if (name === 'quantity') {
      const qty = parseInt(value) || 1
      setFormData(prev => ({ ...prev, quantity: Math.max(1, Math.min(qty, kit.stockCount)) }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Name is required'
    }

    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
      newErrors.customerEmail = 'Invalid email format'
    }

    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required'
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.customerPhone)) {
      newErrors.customerPhone = 'Invalid phone format (use: 123-456-7890)'
    }

    if (formData.quantity < 1) {
      newErrors.quantity = 'Quantity must be at least 1'
    }

    if (formData.quantity > kit.stockCount) {
      newErrors.quantity = `Only ${kit.stockCount} available in stock`
    }

    if (!formData.preferredPickup && !formData.deliveryAddress.trim()) {
      newErrors.deliveryAddress = 'Delivery address is required'
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const orderData: KitOrderData = {
        kitId: kit.id,
        kitName: kit.name,
        kitPrice: kit.price,
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim().toLowerCase(),
        customerPhone: formData.customerPhone.trim(),
        quantity: formData.quantity,
        notes: formData.notes.trim() || undefined,
        preferredPickup: formData.preferredPickup,
        deliveryAddress: !formData.preferredPickup ? formData.deliveryAddress.trim() : undefined,
        paymentMethod: formData.paymentMethod
      }

      const createdOrder = await kitOrderService.createOrder(orderData)
      console.log('✅ Order created successfully:', createdOrder.id)

      // Update stock count
      const newStockCount = kit.stockCount - formData.quantity
      if (onStockUpdate) {
        onStockUpdate(kit.id, newStockCount)
      }

      setCreatedOrderId(createdOrder.id)
      setOrder(createdOrder)
      setShowConfirmation(true)
      onOrderComplete(createdOrder.id)
    } catch (error) {
      console.error('❌ Order creation failed:', error)
      alert(`Failed to create order: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (showConfirmation) {
      setShowConfirmation(false)
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        quantity: 1,
        notes: '',
        preferredPickup: true,
        deliveryAddress: '',
        paymentMethod: 'credit-card'
      })
      setCreatedOrderId('')
      setOrder(null)
    }
    onClose()
  }

  const totalPrice = kit.price * formData.quantity

  if (!isOpen) return null

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ zIndex: 1055 }}
        onClick={handleClose}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            {showConfirmation ? (
              // Confirmation View
              <div className="modal-body text-center py-5">
                <div className="mb-4">
                  <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h3 className="mb-3">Order Submitted Successfully!</h3>
                <p className="text-muted mb-4">
                  Your order ID: <strong>{createdOrderId}</strong>
                </p>
                <div className="alert alert-info mb-4">
                  <i className="fas fa-info-circle me-2"></i>
                  We'll contact you at <strong>{formData.customerEmail}</strong> to arrange{' '}
                  {formData.preferredPickup ? 'pickup' : 'delivery'}.
                </div>
                {order && order.paymentLinkUrl && (
                  <div className="alert alert-success mb-4">
                    <h5 className="alert-heading">
                      <i className="fas fa-credit-card me-2"></i>
                      Complete Your Payment
                    </h5>
                    <p className="mb-3">
                      Click the button below to securely pay for your order via Square:
                    </p>
                    <a
                      href={order.paymentLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-success btn-lg"
                    >
                      <i className="fas fa-lock me-2"></i>
                      Pay ${totalPrice.toFixed(2)} Securely
                    </a>
                    <p className="mt-3 mb-0 small text-muted">
                      Payment link also sent to your email
                    </p>
                  </div>
                )}
                <div className="card bg-light border-0 mb-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3">Order Summary</h5>
                    <div className="row text-start">
                      <div className="col-6">
                        <p className="mb-2"><strong>Kit:</strong></p>
                        <p className="mb-2"><strong>Quantity:</strong></p>
                        <p className="mb-2"><strong>Total:</strong></p>
                        <p className="mb-2"><strong>Payment:</strong></p>
                        <p className="mb-0"><strong>Status:</strong></p>
                      </div>
                      <div className="col-6">
                        <p className="mb-2">{kit.name}</p>
                        <p className="mb-2">{formData.quantity}</p>
                        <p className="mb-2">${totalPrice.toFixed(2)}</p>
                        <p className="mb-2">
                          {formData.paymentMethod === 'credit-card' && 'Credit Card'}
                          {formData.paymentMethod === 'zelle' && 'Zelle'}
                          {formData.paymentMethod === 'venmo' && 'Venmo'}
                          {formData.paymentMethod === 'cash' && 'Cash'}
                        </p>
                        <p className="mb-0"><span className="badge bg-warning">Pending</span></p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-muted small mb-4">
                  <p className="mb-1">
                    <i className="fas fa-store me-2"></i>
                    The Salty Mermaid Art Studio
                  </p>
                  <p className="mb-0">
                    <i className="fas fa-envelope me-2"></i>
                    info@saltymermaidart.com
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            ) : (
              // Order Form View
              <>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">
                    <i className="fas fa-shopping-cart me-2"></i>
                    Order Art Kit
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="modal-body">
                  {/* Kit Info */}
                  <div className="card bg-light border-0 mb-4">
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-3">
                          {kitImage ? (
                            <img
                              src={kitImage.dataUrl}
                              alt={kit.name}
                              className="img-fluid rounded"
                            />
                          ) : (
                            <div className="bg-secondary rounded d-flex align-items-center justify-content-center" style={{ height: '80px' }}>
                              <span className="display-4">🎨</span>
                            </div>
                          )}
                        </div>
                        <div className="col-md-9">
                          <h5 className="mb-2">{kit.name}</h5>
                          <p className="text-muted mb-2">{kit.description}</p>
                          <div className="d-flex align-items-center gap-3">
                            <span className="badge bg-primary">${kit.price.toFixed(2)}</span>
                            <span className="badge bg-success">{kit.stockCount} in stock</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Form */}
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      {/* Customer Information Row */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.customerName ? 'is-invalid' : ''}`}
                          name="customerName"
                          value={formData.customerName}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {errors.customerName && (
                          <div className="invalid-feedback">{errors.customerName}</div>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control ${errors.customerEmail ? 'is-invalid' : ''}`}
                          name="customerEmail"
                          value={formData.customerEmail}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {errors.customerEmail && (
                          <div className="invalid-feedback">{errors.customerEmail}</div>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className={`form-control ${errors.customerPhone ? 'is-invalid' : ''}`}
                          name="customerPhone"
                          value={formData.customerPhone}
                          onChange={handleInputChange}
                          placeholder="123-456-7890"
                          disabled={isSubmitting}
                        />
                        {errors.customerPhone && (
                          <div className="invalid-feedback">{errors.customerPhone}</div>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Quantity <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-control ${errors.quantity ? 'is-invalid' : ''}`}
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleInputChange}
                          min="1"
                          max={kit.stockCount}
                          disabled={isSubmitting}
                        />
                        {errors.quantity && (
                          <div className="invalid-feedback">{errors.quantity}</div>
                        )}
                        <small className="text-muted">Max: {kit.stockCount}</small>
                      </div>

                      {/* Fulfillment and Payment Row */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Fulfillment Method <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex gap-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="preferredPickup"
                              id="pickupRadio"
                              checked={formData.preferredPickup}
                              onChange={() => setFormData(prev => ({ ...prev, preferredPickup: true, deliveryAddress: '' }))}
                              disabled={isSubmitting}
                            />
                            <label className="form-check-label" htmlFor="pickupRadio">
                              <i className="fas fa-store me-1"></i>
                              Pickup
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="preferredPickup"
                              id="deliveryRadio"
                              checked={!formData.preferredPickup}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                preferredPickup: false,
                                paymentMethod: prev.paymentMethod === 'cash' ? 'credit-card' : prev.paymentMethod
                              }))}
                              disabled={isSubmitting}
                            />
                            <label className="form-check-label" htmlFor="deliveryRadio">
                              <i className="fas fa-truck me-1"></i>
                              Delivery
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Payment Method <span className="text-danger">*</span>
                        </label>
                        <div className={`d-flex gap-3 flex-wrap ${errors.paymentMethod ? 'is-invalid' : ''}`}>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="creditCardRadio"
                              value="credit-card"
                              checked={formData.paymentMethod === 'credit-card'}
                              onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                              disabled={isSubmitting}
                            />
                            <label className="form-check-label" htmlFor="creditCardRadio">
                              <i className="fas fa-credit-card me-1"></i>
                              Card
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="zelleRadio"
                              value="zelle"
                              checked={formData.paymentMethod === 'zelle'}
                              onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                              disabled={isSubmitting}
                            />
                            <label className="form-check-label" htmlFor="zelleRadio">
                              <i className="fas fa-mobile-alt me-1"></i>
                              Zelle
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="paymentMethod"
                              id="venmoRadio"
                              value="venmo"
                              checked={formData.paymentMethod === 'venmo'}
                              onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                              disabled={isSubmitting}
                            />
                            <label className="form-check-label" htmlFor="venmoRadio">
                              <i className="fab fa-venmo me-1"></i>
                              Venmo
                            </label>
                          </div>
                          {formData.preferredPickup && (
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="paymentMethod"
                                id="cashRadio"
                                value="cash"
                                checked={formData.paymentMethod === 'cash'}
                                onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                                disabled={isSubmitting}
                              />
                              <label className="form-check-label" htmlFor="cashRadio">
                                <i className="fas fa-money-bill-wave me-1"></i>
                                Cash
                              </label>
                            </div>
                          )}
                        </div>
                        {errors.paymentMethod && (
                          <div className="invalid-feedback d-block">{errors.paymentMethod}</div>
                        )}
                      </div>

                      {/* Delivery Address (conditional) */}
                      {!formData.preferredPickup && (
                        <div className="col-12">
                          <label className="form-label">
                            Delivery Address <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control ${errors.deliveryAddress ? 'is-invalid' : ''}`}
                            name="deliveryAddress"
                            value={formData.deliveryAddress}
                            onChange={handleInputChange}
                            rows={2}
                            placeholder="Enter full delivery address..."
                            disabled={isSubmitting}
                          />
                          {errors.deliveryAddress && (
                            <div className="invalid-feedback">{errors.deliveryAddress}</div>
                          )}
                        </div>
                      )}

                      {/* Notes */}
                      <div className="col-12">
                        <label className="form-label">
                          Special Instructions (Optional)
                        </label>
                        <textarea
                          className="form-control"
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows={2}
                          placeholder="Gift wrap, special handling, etc."
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Total */}
                      <div className="col-12">
                        <div className="card bg-primary text-white">
                          <div className="card-body py-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="mb-0">Total:</h5>
                              <h4 className="mb-0">${totalPrice.toFixed(2)}</h4>
                            </div>
                          </div>
                        </div>
                        <small className="text-muted d-block mt-2">
                          <i className="fas fa-info-circle me-1"></i>
                          Payment processing will be set up in a future update
                        </small>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer border-top">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Submitting Order...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Submit Order
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
